import Ajv from 'ajv'
import yaml from 'js-yaml'

// Initialize AJV for JSON schema validation
const ajv = new Ajv({ allErrors: true })

/**
 * Validate JSON string
 * @param {string} jsonString - The JSON string to validate
 * @returns {Object} - Validation result with isValid and error properties
 */
export const validateJSON = (jsonString) => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return { isValid: false, error: 'Empty JSON input' }
    }

    // Use native JSON.parse for validation with detailed error reporting
    const parsed = JSON.parse(jsonString)
    return { isValid: true, error: null, data: parsed }
  } catch (error) {
    // Enhanced error reporting for JSON parsing
    let errorMessage = error.message
    
    // Try to extract line and column information from error message
    const lineMatch = error.message.match(/position (\d+)/)
    const position = lineMatch ? parseInt(lineMatch[1]) : 0
    
    if (position > 0) {
      const lines = jsonString.substring(0, position).split('\n')
      const line = lines.length
      const column = lines[lines.length - 1].length + 1
      errorMessage = `Invalid JSON at line ${line}, column ${column}: ${error.message}`
    }
    
    return { 
      isValid: false, 
      error: errorMessage,
      line: lineMatch ? lines.length : null,
      column: lineMatch ? lines[lines.length - 1].length + 1 : null
    }
  }
}

/**
 * Format JSON with proper indentation
 * @param {Object} data - The data to format
 * @param {number} indent - Number of spaces for indentation (default: 2)
 * @returns {string} - Formatted JSON string
 */
export const formatJSON = (data, indent = 2) => {
  try {
    return JSON.stringify(data, null, indent)
  } catch (error) {
    throw new Error(`Failed to format JSON: ${error.message}`)
  }
}

/**
 * Minify JSON by removing unnecessary whitespace
 * @param {Object} data - The data to minify
 * @returns {string} - Minified JSON string
 */
export const minifyJSON = (data) => {
  try {
    return JSON.stringify(data)
  } catch (error) {
    throw new Error(`Failed to minify JSON: ${error.message}`)
  }
}

/**
 * Parse JSON string safely
 * @param {string} jsonString - The JSON string to parse
 * @returns {Object} - Parsed object or null if invalid
 */
export const parseJSON = (jsonString) => {
  try {
    return JSON.parse(jsonString)
  } catch (error) {
    return null
  }
}

/**
 * Convert JSON to YAML
 * @param {Object} data - The data to convert
 * @returns {string} - YAML string
 */
export const jsonToYaml = (data) => {
  try {
    return yaml.dump(data, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false
    })
  } catch (error) {
    throw new Error(`Failed to convert to YAML: ${error.message}`)
  }
}

/**
 * Convert YAML to JSON
 * @param {string} yamlString - The YAML string to convert
 * @returns {Object} - Parsed JSON object
 */
export const yamlToJson = (yamlString) => {
  try {
    return yaml.load(yamlString)
  } catch (error) {
    throw new Error(`Failed to convert from YAML: ${error.message}`)
  }
}

/**
 * Get JSON size in bytes
 * @param {string} jsonString - The JSON string
 * @returns {number} - Size in bytes
 */
export const getJsonSize = (jsonString) => {
  return new Blob([jsonString]).size
}

/**
 * Get JSON statistics
 * @param {Object} data - The parsed JSON data
 * @returns {Object} - Statistics about the JSON
 */
export const getJsonStats = (data) => {
  if (!data || typeof data !== 'object') {
    return { keys: 0, depth: 0, size: 0 }
  }

  const stats = {
    keys: 0,
    depth: 0,
    size: 0,
    types: {}
  }

  const countKeys = (obj, depth = 0) => {
    stats.depth = Math.max(stats.depth, depth)
    
    if (Array.isArray(obj)) {
      stats.types.array = (stats.types.array || 0) + 1
      obj.forEach(item => {
        if (typeof item === 'object' && item !== null) {
          countKeys(item, depth + 1)
        }
      })
    } else if (typeof obj === 'object' && obj !== null) {
      stats.types.object = (stats.types.object || 0) + 1
      Object.keys(obj).forEach(key => {
        stats.keys++
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          countKeys(obj[key], depth + 1)
        }
      })
    } else {
      const type = typeof obj
      stats.types[type] = (stats.types[type] || 0) + 1
    }
  }

  countKeys(data)
  stats.size = JSON.stringify(data).length

  return stats
}

/**
 * Search in JSON data
 * @param {Object} data - The JSON data to search in
 * @param {string} searchTerm - The term to search for
 * @param {boolean} caseSensitive - Whether search should be case sensitive
 * @returns {Array} - Array of matching paths
 */
export const searchInJson = (data, searchTerm, caseSensitive = false) => {
  if (!data || !searchTerm) return []
  
  const results = []
  const searchRegex = new RegExp(
    caseSensitive ? searchTerm : searchTerm.toLowerCase(),
    'g'
  )

  const searchRecursive = (obj, path = '') => {
    if (typeof obj === 'string') {
      const text = caseSensitive ? obj : obj.toLowerCase()
      if (text.includes(searchTerm)) {
        results.push({
          path,
          value: obj,
          type: 'string'
        })
      }
    } else if (typeof obj === 'number') {
      const text = obj.toString()
      if (text.includes(searchTerm)) {
        results.push({
          path,
          value: obj,
          type: 'number'
        })
      }
    } else if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        searchRecursive(item, `${path}[${index}]`)
      })
    } else if (typeof obj === 'object' && obj !== null) {
      Object.keys(obj).forEach(key => {
        const newPath = path ? `${path}.${key}` : key
        searchRecursive(obj[key], newPath)
      })
    }
  }

  searchRecursive(data)
  return results
}
